import { getAuth, signInWithCustomToken } from 'firebase/auth'
import { push, child, ref, onValue, set, off } from 'firebase/database'
import db from 'domains/db'
import { Auth } from 'aws-amplify'
import { jwtDecode } from 'jwt-decode'

//Firebase auth functions

// How long to wait for the backend to return a custom token before giving up.
// A healthy exchange takes a few seconds. This is generous on purpose: the point is
// to put an upper bound on the wait, not to fail fast.
export const CUSTOM_TOKEN_TIMEOUT_MS = 20000

//Provides a 60-min Firebase Token, and a refresh token to get new FB tokens.
//https://firebase.google.com/docs/auth/admin/manage-sessions
export const firebaseAuthWithCustomToken = (token) => signInWithCustomToken(getAuth(), token)

/**
 * Asks the backend for a Firebase custom token, by writing a request to the Realtime
 * Database and waiting for a token to be written back.
 *
 * Sends the Auth0 ID token, and goes to authWithIdToken rather than the older
 * authWithOAuthToken. The old function took an access token and called Auth0's
 * /userinfo endpoint just to read the user's id out of it, and that endpoint allows
 * only 5 requests a minute per user. Signing the same account in repeatedly ran into
 * that limit, Auth0 returned an error, and the backend had no token to give back. An
 * ID token already carries the user's id and is signed, so the new function reads it
 * from the token and never calls Auth0 at all.
 *
 * authWithOAuthToken still exists and is unchanged, because the mobile app uses it.
 *
 * Rejects rather than waiting indefinitely. It used to only ever resolve, so if the
 * response never arrived the promise never settled, Firebase sign-in never happened,
 * and the app sat on its loading spinner permanently with no error anywhere.
 */
export const getFirebaseCustomToken = (idToken) => {
  return new Promise((resolve, reject) => {
    const authKey = push(child(ref(db), 'requests/authWithIdToken')).key
    const authPath = `requests/authWithIdToken/${authKey}`
    const responseRef = ref(db, `${authPath}/response`)

    let settled = false
    let timeoutId = null

    // Whichever of the three outcomes below happens first wins, and the other two are
    // ignored. Detaching uses the ref the listener was attached to: detaching the parent
    // path, as this used to, leaves the listener in place.
    const settle = (finish, value) => {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)
      off(responseRef)
      finish(value)
    }

    timeoutId = setTimeout(
      () => settle(reject, new Error(`No Firebase custom token after ${CUSTOM_TOKEN_TIMEOUT_MS}ms`)),
      CUSTOM_TOKEN_TIMEOUT_MS
    )

    onValue(responseRef, (snap) => {
      // Firebase calls this straight away with null, before the backend has written
      // anything, so an empty response is not an answer.
      const response = snap.val()
      if (!response) return

      if (response.token) {
        settle(resolve, response.token)
      } else if (response.error) {
        // The backend reports a failure by writing an error here instead of a token.
        // This used to be ignored, because the only thing being looked for was a token,
        // so a reported failure was indistinguishable from no reply at all.
        settle(reject, new Error(response.error.msg || 'The backend could not issue a Firebase token'))
      }
    })

    // The shape authWithIdToken expects. It reads the user's id from the token itself,
    // so unlike the old request there is no separate userId to send.
    set(ref(db, authPath), { idToken }).catch((err) => settle(reject, err))
  })
}

// Throws if either step fails. The caller is responsible for handling that — see
// onAuth0Success in modules/Auth/AuthContext.js.
//
// Deliberately does not retry. An earlier version of this change retried once and looked
// like it fixed things, but that was misleading: the retry only ever succeeded because it
// happened 20 seconds later, after waiting out the old timeout. Once the backend's error
// response is read as soon as it arrives, an immediate second attempt fails just as the
// first did. A retry would need a delay to be worth anything, and the right delay depends
// on why the backend is failing, which is not yet understood.
export const firebaseAuth = async (idToken) => {
  const customToken = await getFirebaseCustomToken(idToken)
  return firebaseAuthWithCustomToken(customToken)
}
//End firebase

//AWS auth functions
export async function awsAuth(idToken, userId) {
  const domain = process.env.AUTH_DOMAIN
  const { exp } = jwtDecode(idToken)

  try {
    const cred = await Auth.federatedSignIn(
      domain,
      {
        token: idToken, //Amplify docs say to use idToken
        expires_at: exp * 1000,
      },
      {
        name: userId,
      }
    )

    if (cred) {
      console.log('Federated Sign In success')
      return { success: true }
    } else {
      console.log('Federated Sign In - credentials not found')
      return { success: false, error: 'Federated Sign In did not return credentials' }
    }
  } catch (error) {
    console.error('error', error)
    return { success: false, error }
  }
}
//End AWS
